import { useEffect, useState } from "react";
import BarcodeScannerModal from "../components/BarcodeScannerModal";
import { getAllProducts } from "../services/dataCache";
import { assignBarcode, clearBarcode, findByBarcode } from "../services/barcodeService";

export default function AdminBarcodeMapping() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScanner, setShowScanner] = useState(false);
  const [scannedCode, setScannedCode] = useState("");
  const [search, setSearch] = useState("");
  const [flash, setFlash] = useState("");

  useEffect(() => {
    async function load() {
      setProducts(await getAllProducts()); // session cache — 0 reads on revisit
      setLoading(false);
    }
    load();
  }, []);

  function popFlash(msg) {
    setFlash(msg);
    setTimeout(() => setFlash(""), 3500);
  }

  function handleScanned(code) {
    setScannedCode(code);
    setShowScanner(false);
    setSearch("");
  }

  // Where is this scanned code right now? (Searches ALL products, active or
  // not — Brian's field finding: an accidental mapping onto a no-stock item
  // was invisible here before.)
  const currentHolder = scannedCode ? findByBarcode(products, scannedCode) : null;

  async function handleAssign(target) {
    // Overwrite guard: target already carries a different barcode
    if (target.barcode && String(target.barcode) !== String(scannedCode)) {
      const ok = window.confirm(
        `${target.sku} — ${target.name}\nalready has barcode ${target.barcode}.\n\nReplace it with ${scannedCode}?`
      );
      if (!ok) return;
    }
    // Move guard: the code lives on another product; assigning MOVES it
    if (currentHolder && currentHolder.sku !== target.sku) {
      const ok = window.confirm(
        `Barcode ${scannedCode} is currently on:\n${currentHolder.sku} — ${currentHolder.name}\n\nMove it to ${target.sku} — ${target.name}?`
      );
      if (!ok) return;
    }
    await assignBarcode(target.sku, scannedCode, products);
    setProducts(prev => prev.map(p => {
      if (p.sku === target.sku) return { ...p, barcode: String(scannedCode), barcodeSource: "portal" };
      if (p.barcode && findByBarcode([p], scannedCode)) {
        const { barcode, barcodeSource, ...rest } = p;
        return rest;
      }
      return p;
    }));
    popFlash(`Barcode ${scannedCode} → ${target.sku} ${target.name}`);
    setScannedCode("");
    setSearch("");
  }

  async function handleClearHolder() {
    if (!currentHolder) return;
    if (!window.confirm(`Remove barcode ${scannedCode} from ${currentHolder.sku} — ${currentHolder.name}?`)) return;
    await clearBarcode(currentHolder.sku);
    setProducts(prev => prev.map(p => {
      if (p.sku !== currentHolder.sku) return p;
      const { barcode, barcodeSource, ...rest } = p;
      return rest;
    }));
    popFlash(`Barcode removed from ${currentHolder.sku}`);
  }

  const mappedCount = products.filter(p => p.barcode).length;

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.sku.includes(search)
  );

  // Reverse flow: barcodes mapped in the FIELD get typed into QuickBooks
  // later. This exports exactly the ones QB doesn't have yet.
  const portalMapped = products.filter(p => p.barcode && p.barcodeSource === "portal");
  function exportForQB() {
    const rows = [["Item", "Name", "Barcode"], ...portalMapped.map(p => [p.sku, p.name, p.barcode])];
    const csv = rows
      .map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }); // BOM keeps Korean intact in Excel
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `portal-barcodes-for-qb-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  if (loading) return <p>Loading products...</p>;

  return (
    <div className="admin-barcode">
      <h2>Barcode Mapping ({mappedCount} / {products.length} mapped)</h2>
      <p className="inventory-help">
        Scan a product's barcode. If it's already mapped you'll see which product holds it
        (and can clear or move it); otherwise search below and select the right product.
      </p>

      <button className="scan-btn" onClick={() => setShowScanner(true)}>
        📷 Scan Barcode
      </button>

      {portalMapped.length > 0 && (
        <div className="export-for-qb">
          <button className="scan-btn" onClick={exportForQB}>
            ⬇ Export {portalMapped.length} new mapping{portalMapped.length !== 1 ? "s" : ""} for QuickBooks
          </button>
          <p className="inventory-help-note">
            These barcodes were mapped on the portal but aren't in QuickBooks yet.
            Download the list, enter them into QB when convenient — once the daily
            item-list upload brings them back, they drop off this list automatically.
          </p>
        </div>
      )}

      {flash && <p className="inventory-success">{flash}</p>}

      {scannedCode && (
        <div className="scanned-code-box">
          <p>Scanned code: <strong>{scannedCode}</strong></p>

          {currentHolder ? (
            <div className="current-holder-box">
              <p>
                Currently mapped to: <strong>{currentHolder.sku} — {currentHolder.name}</strong>
                {!currentHolder.active && <span className="already-mapped"> (inactive / no stock)</span>}
              </p>
              <button className="mini-btn danger" onClick={handleClearHolder}>
                Remove this mapping
              </button>
            </div>
          ) : (
            <p className="inventory-help-note">This barcode isn't mapped to anything yet.</p>
          )}

          <p>{currentHolder ? "Or move it to a different product:" : "Search and select the matching product:"}</p>
          <input
            placeholder="Search by name or SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="barcode-match-list">
            {search && filtered.slice(0, 10).map(p => (
              <div key={p.sku} className="barcode-match-row" onClick={() => handleAssign(p)}>
                <span className="sku">{p.sku}</span>
                <span>{p.name}{!p.active ? " (inactive)" : ""}</span>
                {p.barcode && <span className="already-mapped">has barcode</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {showScanner && (
        <BarcodeScannerModal onScan={handleScanned} onClose={() => setShowScanner(false)} />
      )}
    </div>
  );
}
